import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Clock, MailCheck, CheckCircle2, XCircle, PauseCircle, Loader2, LogOut } from "lucide-react";
import { api, errorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/lib/i18n";
import { Logo, LangToggle } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

export const isApproved = (u) => u && (u.platform_role === "platform_admin" || (u.approval_status === "APPROVED" && u.email_verified));

const ICON = { PENDING_REVIEW: Clock, WAITLISTED: PauseCircle, MORE_INFO_REQUIRED: MailCheck, REJECTED: XCircle, SUSPENDED: XCircle, ARCHIVED: XCircle };

function StatusCopy({ user }) {
  const { t } = useI18n();
  if (!user.email_verified) return <><h1 className="text-3xl font-extrabold tracking-tight">{t("Confirm your email address")}</h1><p className="text-muted-foreground mt-3">{t("We sent a confirmation link to {{email}}. Your application is sent to our team once your email is confirmed.", { email: user.email })}</p></>;
  const copy = {
    PENDING_REVIEW: [t("Your registration is awaiting approval"), t("Thank you for registering with TOPTALENT. Your application has been submitted successfully and is awaiting review by our administration team. We will notify you by email once your account has been approved.")],
    WAITLISTED: [t("Your registration is on the waiting list"), t("Your registration has been placed on our waiting list. We will get back to you as soon as possible.")],
    MORE_INFO_REQUIRED: [t("Additional information required"), t("To complete the review of your registration, our team needs additional information.")],
    REJECTED: [t("Your registration was not approved"), t("After review, we are unable to approve your registration at this time.")],
    SUSPENDED: [t("Your account is suspended"), t("Access to your account has been suspended by the TOPTALENT administration.")],
    ARCHIVED: [t("Your registration was not approved"), t("After review, we are unable to approve your registration at this time.")],
  }[user.approval_status] || ["", ""];
  return <><h1 className="text-3xl font-extrabold tracking-tight" data-testid="pending-title">{copy[0]}</h1><p className="text-muted-foreground mt-3" data-testid="pending-text">{copy[1]}</p></>;
}

function InfoReply({ onDone }) {
  const { t } = useI18n();
  const [msg, setMsg] = useState("");
  const send = async () => { try { await api.post("/auth/registration/info", { message: msg }); toast.success(t("Sent")); onDone(); } catch (e) { toast.error(errorDetail(e)); } };
  return <div className="mt-5 space-y-2"><Textarea rows={4} value={msg} onChange={(e) => setMsg(e.target.value)} placeholder={t("Your answer")} data-testid="info-reply-text" />
    <Button onClick={send} disabled={!msg.trim()} data-testid="info-reply-submit">{t("Send my answer")}</Button></div>;
}

export function PendingApproval() {
  const { user, refresh, signOut } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  useEffect(() => { if (isApproved(user)) nav(user.account_type === "candidate" ? "/me" : "/app", { replace: true }); }, [user, nav]);
  if (!user) return null;
  const Icon = user.email_verified ? ICON[user.approval_status] || Clock : MailCheck;
  const resend = async () => { try { await api.post("/auth/verify-email/resend"); toast.success(t("Confirmation email sent")); } catch (e) { toast.error(errorDetail(e)); } };
  return (
    <div className="min-h-screen bg-background">
      <header className="h-16 px-6 flex items-center justify-between border-b"><Logo /><div className="flex items-center gap-3"><LangToggle />
        <button onClick={signOut} className="text-xs text-muted-foreground flex items-center gap-1" data-testid="pending-logout"><LogOut className="w-3.5 h-3.5" />{t("Sign out")}</button></div></header>
      <main className="max-w-xl mx-auto px-6 py-20 fade-up" data-testid="pending-page">
        <div className="w-12 h-12 rounded-2xl bg-accent flex items-center justify-center mb-6"><Icon className="w-6 h-6 text-primary" /></div>
        <StatusCopy user={user} />
        {user.approval_reason && <blockquote className="mt-4 border-l-2 border-primary pl-4 text-sm italic" data-testid="pending-reason">{user.approval_reason}</blockquote>}
        {user.approval_status === "MORE_INFO_REQUIRED" && <InfoReply onDone={refresh} />}
        <div className="mt-8 flex flex-wrap gap-2">
          {!user.email_verified && <Button variant="outline" onClick={resend} data-testid="resend-verification">{t("Resend the confirmation email")}</Button>}
          <Button variant="ghost" onClick={refresh} data-testid="pending-refresh">{t("Refresh status")}</Button>
        </div>
        <p className="text-xs text-muted-foreground mt-10">{t("Questions? Write to us at")} <a className="text-primary" href="mailto:contact@toptalent.io">{/* i18n-ignore */}contact@toptalent.io</a></p>
      </main>
    </div>
  );
}

export function VerifyEmail() {
  const { t } = useI18n();
  const [params] = useSearchParams();
  const { refresh } = useAuth();
  const [state, setState] = useState("loading");
  const [err, setErr] = useState("");
  useEffect(() => {
    api.post("/auth/verify-email", { token: params.get("token") || "" }).then(() => { setState("ok"); refresh(); }).catch((e) => { setErr(errorDetail(e)); setState("error"); });
  }, []); // eslint-disable-line
  return (
    <div className="min-h-screen flex items-center justify-center px-6"><div className="max-w-md text-center" data-testid="verify-email-page">
      {state === "loading" && <Loader2 className="w-6 h-6 animate-spin text-primary mx-auto" />}
      {state === "ok" && <><CheckCircle2 className="w-10 h-10 text-emerald-600 mx-auto" /><h1 className="text-2xl font-bold mt-4" data-testid="verify-ok">{t("Email confirmed")}</h1>
        <p className="text-muted-foreground mt-2">{t("Your application has been sent to our team. You will receive an email once your account has been approved.")}</p></>}
      {state === "error" && <><XCircle className="w-10 h-10 text-red-600 mx-auto" /><p className="mt-4" data-testid="verify-error">{err}</p></>}
      <Button asChild variant="outline" className="mt-6"><Link to="/pending">{t("View my registration status")}</Link></Button>
    </div></div>
  );
}
